import { LAYOUT_SCHEMA_VERSION } from "@hq/contracts";
import { activePane, firstGroup, idGen } from "./tree";
import type { IdOptions, LayoutNode, LayoutTree, TabGroup } from "./types";

/**
 * Returns `count` positive ratios summing to 1. Falls back to equal ratios when the input has
 * the wrong length or contains a non-positive or non-finite value.
 */
export function normalizeRatios(ratios: readonly number[], count: number): number[] {
  if (count === 0) return [];
  const valid = ratios.length === count && ratios.every((r) => Number.isFinite(r) && r > 0);
  const base = valid ? [...ratios] : Array.from({ length: count }, () => 1);
  const sum = base.reduce((acc, r) => acc + r, 0);
  // Leave input that already sums to 1 untouched so normalization is a fixed point.
  if (Math.abs(sum - 1) < 1e-12) return base;
  return base.map((r) => r / sum);
}

function normalizeGroup(group: TabGroup): TabGroup | null {
  if (group.tabs.length === 0) return null;
  const active = group.tabs.some((tab) => tab.id === group.activeTabId)
    ? group.activeTabId
    : group.tabs[0]?.id;
  const out: TabGroup = { type: "tabs", id: group.id, tabs: group.tabs };
  if (active !== undefined) out.activeTabId = active;
  return out;
}

type Weighted = { node: LayoutNode; weight: number };

function normalizeNode(node: LayoutNode): LayoutNode | null {
  if (node.type === "tabs") return normalizeGroup(node);
  const weights = normalizeRatios(node.ratios, node.children.length);
  const kept: Weighted[] = [];
  node.children.forEach((child, i) => {
    const weight = weights[i] ?? 0;
    const normalized = normalizeNode(child);
    if (!normalized) return;
    if (normalized.type === "split" && normalized.direction === node.direction) {
      // Flatten a nested split with the same direction into its parent.
      normalized.children.forEach((grandchild, j) => {
        kept.push({ node: grandchild, weight: weight * (normalized.ratios[j] ?? 0) });
      });
    } else {
      kept.push({ node: normalized, weight });
    }
  });
  const only = kept[0];
  if (only === undefined) return null;
  if (kept.length === 1) return only.node;
  return {
    type: "split",
    id: node.id,
    direction: node.direction,
    children: kept.map((k) => k.node),
    ratios: normalizeRatios(
      kept.map((k) => k.weight),
      kept.length,
    ),
  };
}

function setActive(node: LayoutNode, paneId: string): LayoutNode {
  if (node.type === "tabs") {
    if (node.activeTabId === paneId || !node.tabs.some((tab) => tab.id === paneId)) return node;
    return { ...node, activeTabId: paneId };
  }
  return { ...node, children: node.children.map((child) => setActive(child, paneId)) };
}

function containsPane(node: LayoutNode, paneId: string): boolean {
  if (node.type === "tabs") return node.tabs.some((tab) => tab.id === paneId);
  return node.children.some((child) => containsPane(child, paneId));
}

/**
 * Restores every structural invariant: ratios are positive and sum to 1, empty groups are
 * removed (except a lone root), single-child splits collapse, nested same-direction splits are
 * flattened, every group's `activeTabId` points at one of its tabs, and `focusedPaneId` points
 * at an existing pane that is the active tab of its group.
 */
export function normalize(tree: LayoutTree, opts: IdOptions = {}): LayoutTree {
  let root = normalizeNode(tree.root);
  if (!root) {
    root =
      tree.root.type === "tabs"
        ? { type: "tabs", id: tree.root.id, tabs: [] }
        : { type: "tabs", id: idGen(opts)(), tabs: [] };
  }
  const focused =
    tree.focusedPaneId !== undefined && containsPane(root, tree.focusedPaneId)
      ? tree.focusedPaneId
      : firstGroupPane(root);
  const out: LayoutTree = { version: LAYOUT_SCHEMA_VERSION, root };
  if (focused !== undefined) {
    out.root = setActive(root, focused);
    out.focusedPaneId = focused;
  }
  return out;
}

function firstGroupPane(root: LayoutNode): string | undefined {
  const group = firstGroup(root);
  return group ? activePane(group)?.id : undefined;
}
